/**
 * Mensajes: labels, validación, auth, errores, hints y paginación.
 */
// --- Labels comunes (botones, acciones) ---
export const labels = {
  cancel: 'Cancelar',
  save: 'Guardar',
  delete: 'Eliminar',
  close: 'Cerrar',
  confirm: 'Confirmar',
  back: 'Volver',
  loading: 'Cargando...',
  saving: 'Guardando...',
  deleting: 'Eliminando...',
  processing: 'Procesando...',
  loadingPets: 'Cargando mascotas...',
  loadingClinicInfo: 'Cargando información de clínica...',
} as const;

// --- Validación ---
export const validation = {
  required: 'Este campo es obligatorio',
  requiredFields: (fields: string) => `Los siguientes campos son obligatorios: ${fields}`,
  minLength: (n: number) => `Mínimo ${n} caracteres`,
  minLengthNoSpaces: (n: number) => `El motivo debe tener al menos ${n} caracteres (sin contar espacios). Por favor, proporciona más detalles.`,
  maxLengthNoSpaces: (n: number) => `El motivo no puede exceder ${n} caracteres (sin contar espacios)`,
  maxLength: (n: number) => `Máximo ${n} caracteres`,
  emailInvalid: 'Por favor ingresa un email válido.',
  passwordMinLength: 'La contraseña debe tener al menos 8 caracteres.', // pragma: allowlist secret
  writeResponse: 'Por favor, escribe una respuesta',
  provideReason: 'Por favor, indica el motivo por el cual estás desactivando tu cuenta',
  deletionReasonRequired: 'El motivo de eliminación es obligatorio. Por favor, ingresa un motivo antes de eliminar.',
  deletionReasonMinLength: 'El motivo debe tener al menos 3 caracteres.',
  rejectionReasonRequired: 'Debes proporcionar un motivo de rechazo',
  cancellationReasonRequired: 'Por favor ingresa un motivo de cancelación',
  selectClinic: 'Por favor selecciona una clínica',
  selectPet: 'Por favor selecciona una mascota',
  selectVeterinarian: 'Por favor selecciona un veterinario',
  selectItem: 'Seleccione un item para registrar movimientos',
  nameRequired: 'El nombre es obligatorio',
  planNameRequired: 'El nombre del plan es requerido',
  planPetsMax: 'El máximo de mascotas debe ser mayor a 0',
  planPriceNonNegative: 'El precio no puede ser negativo',
  planSummaryLimit: 'El límite de uso de resumen clínico debe ser mayor a 0',
  planFilesLimit: 'El límite de archivos por mascota debe ser mayor a 0',
  completeRequiredFields: 'Por favor completa todos los campos requeridos.',
  completeInterviewFields: 'Por favor completa todos los campos requeridos',
  atLeastOneSpecies: 'Debe haber al menos una especie habilitada',
  vaccineDateRequired: 'Por favor ingresa una fecha para la vacuna',
  interviewInfoRequired: 'Debes ingresar al menos alguna información en la entrevista',
} as const;

/** Modal de términos (carga desde API legal). */
export const termsModal = {
  defaultTitle: 'Términos y Condiciones',
  loading: 'Cargando términos…',
  loadError:
    'No se pudieron cargar los términos. Revisa tu conexión e inténtalo de nuevo.',
  retry: 'Reintentar',
  lastUpdatedLabel: 'Última actualización:',
} as const;

/** Textos de ayuda en ajustes del sistema (admin). */
export const systemSettingsHints = {
  hoursOnly: '(solo horas, sin minutos)',
  enabledSpeciesHelp:
    'Solo las especies seleccionadas aparecerán en el formulario de registro de mascotas.',
} as const;

// --- Validación: auth (login, registro, recuperar / restablecer contraseña) ---
export const authValidation = {
  emailRequired: 'El correo es obligatorio',
  emailMax: 'Máximo 254 caracteres',
  emailInvalid: 'Correo electrónico no válido',
  passwordMin: 'La contraseña debe tener al menos 8 caracteres', // pragma: allowlist secret
  passwordMax: 'Máximo 128 caracteres', // pragma: allowlist secret
  passwordsDontMatch: 'Las contraseñas no coinciden', // pragma: allowlist secret
  confirmPasswordRequired: 'Confirma tu contraseña', // pragma: allowlist secret
  acceptTerms: 'Debes aceptar los términos y condiciones',
  roleInvalid: 'Rol no válido',
  fullNameMin: 'Mínimo 2 caracteres',
  fullNameMax: 'Máximo 100 caracteres',
  optionalMax: (max: number) => `Máximo ${max} caracteres`,
  passwordStrength: 'La contraseña debe contener: mayúsculas, minúsculas, números y caracteres especiales', // pragma: allowlist secret
} as const;

// --- Errores genéricos (fallback cuando el backend no devuelve detalle) ---
export const errors = {
  generic: 'Error desconocido',
  save: 'Error al guardar',
  saveResponse: 'Error al guardar la respuesta',
  update: 'Error al actualizar',
  delete: 'Error al eliminar',
  load: 'Error al cargar',
  loadPayments: 'Error al cargar pagos pendientes',
  loadDetail: 'Error al cargar detalle',
  updateConfig: 'Error al actualizar la configuración',
  deactivateAccount: 'No se pudo desactivar la cuenta',
  deleteReminder: 'Error al eliminar el recordatorio',
  updateReminder: 'Error al actualizar el recordatorio',
  saveReminder: 'Error al guardar el recordatorio',
  approvePayment: 'Error al aprobar pago',
  rejectPayment: 'Error al rechazar pago',
  createAppointment: 'Error al crear la cita',
  updateAppointment: 'Error al actualizar la cita',
  cancelAppointment: 'Error al cancelar la cita',
  searchPets: 'Error al buscar mascotas',
  determineClinic: 'No se pudo determinar la clínica. Por favor, recarga la página.',
  processAI: 'Error al procesar la información',
  createNotification: 'Error al crear la notificación',
  sendNotification: 'Error al enviar la notificación',
  deleteNotification: 'Error al eliminar la notificación',
  createControl: 'Error al crear el control médico',
  registerVaccine: 'Error al registrar la vacuna',
  processVaccinations: 'Error al procesar vacunaciones/cirugías',
  processControls: 'Error al procesar controles médicos',
  openForm: 'Error al abrir el formulario',
  unexpected: 'Error inesperado al procesar la solicitud',
  deleteDraft: 'Error al eliminar borrador',
  validation: 'Error de validación',
  readImage: 'Error al leer la imagen',
  registerUser: 'Error al registrar usuario',
} as const;

// --- Placeholders / hints ---
export const hints = {
  passwordMinPlaceholder: 'Mínimo 8 caracteres', // pragma: allowlist secret
  charactersCount: (current: number, max: number) => `${current}/${max} caracteres`,
  minCharacters: (n: number) => `(mínimo ${n})`,
} as const;

// --- Paginación genérica (tablas, listas) ---
export const pagination = {
  showing: (start: number, end: number, total: number, itemLabel: string) =>
    `Mostrando ${start} a ${end} de ${total} ${itemLabel}`,
  page: (current: number, total: number) => `Página ${current} de ${total}`,
  prev: 'Anterior',
  next: 'Siguiente',
} as const;
